/**
 * fab-room-compositor.js
 *
 * Canvas compositor for the PDP size picker room mock.
 * Art print rendering is kept on the existing path.
 * Canvas rendering has its own frame, gap, depth and shadow controls.
 */

(function () {
  'use strict';

  // ====================================================================
  // Product geometry
  // ====================================================================

  // Art print moulding, in real-world cm. This is only used by art prints.
  var MOULDING_CM = 2;

  // Canvas float frame geometry, in real-world cm.
  // The frame and gap are added outside the selected canvas face size.
  var CANVAS_FLOAT_FRAME_CM = 1.2; // visible float-frame face width
  var CANVAS_FLOAT_GAP_CM   = 1.00; // reveal between frame and canvas

  // Non-full-bleed art prints have a small white paper border.
  var PAPER_BORDER_FRACTION = 0.05;

  // ====================================================================
  // Frame assets and colours
  // ====================================================================

  var FRAME_PNGS = {
    black:  { portrait: '', landscape: '' },
    white:  { portrait: '', landscape: '' },
    oak:    { portrait: '', landscape: '' },
    brown:  { portrait: '', landscape: '' },
    silver: { portrait: '', landscape: '' },
    gold:   { portrait: '', landscape: '' }
  };

  var FRAME_PNG_MOULDING_FRACTION = 0.09;

  var FLAT_MOULDING = {
    black:  { face: '#222020', light: '#3a3735', dark: '#0c0b0b' },
    white:  { face: '#f4f1ea', light: '#ffffff', dark: '#cfc9bd' },
    oak:    { face: '#dec094', light: '#efe0c0', dark: '#b89868' },
    brown:  { face: '#6b4423', light: '#8a5f3f', dark: '#4d3015' },
    silver: { face: '#c8c5c4', light: '#e8e6e5', dark: '#969392' },
    gold:   { face: '#f5ca7a', light: '#fae0ac', dark: '#caa04e' }
  };

  // ====================================================================
  // Light and colour matching
  // ====================================================================

  var LIGHT_OPACITY = 0.16;
  var LIGHT_LUM_BOOST = 0.10;

  var ART_BLUR_PX = 0.3;
  var EDGE_FEATHER_ALPHA = 0.18;
  // Existing image-only wall matching can be reduced because we now tint
  // the whole object too.
  var WALL_MATCH_ALPHA = 0.04;

  // ====================================================================
  // Shadow tuning
  // ====================================================================

  // ROOM INTEGRATION (art prints + canvas)
  // Generic settings. These apply to every room, using each scene's own
  // wallRgb and wallLum from wall_area JSON.
  var ART_PRINT_WALL_SOFTLIGHT_ALPHA = 8.14;
  var ART_PRINT_WALL_MULTIPLY_ALPHA = 0.025;
  var ART_PRINT_WALL_SCREEN_ALPHA = 0.018;
  var ART_PRINT_WALL_INTEGRATION_FRAMED_MULT = 8.12;
  var ART_PRINT_WALL_INTEGRATION_UNFRAMED_MULT = 8.70;

  // Optional: let wall luminance slightly affect the wall shadow.
  // Lighter walls show shadows more clearly; darker walls avoid fake black halos.
  var ART_PRINT_SHADOW_WALL_LUM_RESPONSE = 0.28;

  // ART PRINT SHADOWS
  // Used only by drawContactShadow(), which is called by the art-print path.
  // Canvas shadows are controlled separately below.
  var ART_PRINT_SHADOW_OPACITY = 0.32;
  var ART_PRINT_SHADOW_BLUR_MIN_PX = 16;
  var ART_PRINT_SHADOW_BLUR_WIDTH_FACTOR = 0.11;
  var ART_PRINT_SHADOW_SIDE_FACTOR = 0.045;
  var ART_PRINT_SHADOW_AMBIENT_SIDE_FACTOR = 0.018;
  var ART_PRINT_SHADOW_DOWN_FACTOR = 0.036;
  var ART_PRINT_SHADOW_TOP_DOWN_MULTIPLIER = 1.35;
  var ART_PRINT_SHADOW_PAD_MIN_PX = 2;
  var ART_PRINT_SHADOW_PAD_MOULDING_FACTOR = 0.25;

  // Extra wall-shadow strength when a frame moulding is present.
  // These are framed art prints only.
  var ART_PRINT_FRAMED_SHADOW_OPACITY_MULT = 1.2;
  var ART_PRINT_FRAMED_SHADOW_OFFSET_MULT = 2;
  var ART_PRINT_FRAMED_SHADOW_BLUR_MULT = 1.35;

  // ART PRINT INNER FRAME SHADOW
  // Shadow cast from the inside edge of the frame onto the print/artwork.
  // This is different from ART_PRINT_SHADOW_* which controls the whole
  // object's wall shadow.
  var ART_PRINT_INNER_FRAME_SHADOW_OPACITY = 0.17; // was hardcoded as 0.32
  var ART_PRINT_INNER_FRAME_SHADOW_WIDTH_FACTOR = 0.16; // was hardcoded as 0.22
  var ART_PRINT_INNER_FRAME_SHADOW_MIN_PX = 1.5;

  // CANVAS SHADOW DEPTH
  // These values do not change the visual size of the canvas. They only
  // feed shadow strength, offset and blur.
  var CANVAS_FRAMED_SHADOW_DEPTH_CM = 4.2;
  var CANVAS_UNFRAMED_SHADOW_DEPTH_CM = 100.0;
  var CANVAS_FRAMED_SHADOW_DEPTH_FALLBACK_FACTOR = 0.070;
  var CANVAS_UNFRAMED_SHADOW_DEPTH_FALLBACK_FACTOR = 0.75;

  // CANVAS SHADOW DIRECTION
  // scene.lightDirection controls shadow direction.
  // scene.wallArea.wallLum also nudges art-print wall shadow opacity.
  var CANVAS_FRAMED_SHADOW_SIDE_FACTOR = 0.24;
  var CANVAS_FRAMED_SHADOW_SIDE_MIN_PX = 1;
  var CANVAS_FRAMED_SHADOW_SIDE_MAX_PX = 10;
  var CANVAS_FRAMED_SHADOW_DOWN_FACTOR = 0.32;
  var CANVAS_FRAMED_SHADOW_DOWN_MIN_PX = 1.5;
  var CANVAS_FRAMED_SHADOW_DOWN_MAX_PX = 12;

  var CANVAS_UNFRAMED_SHADOW_SIDE_FACTOR = 0.18;
  var CANVAS_UNFRAMED_SHADOW_SIDE_MIN_PX = 1;
  var CANVAS_UNFRAMED_SHADOW_SIDE_MAX_PX = 6;
  var CANVAS_UNFRAMED_SHADOW_DOWN_FACTOR = 0.24;
  var CANVAS_UNFRAMED_SHADOW_DOWN_MIN_PX = 1.5;
  var CANVAS_UNFRAMED_SHADOW_DOWN_MAX_PX = 8;

  var CANVAS_SHADOW_TOP_DOWN_MULTIPLIER = 1.15;
  var CANVAS_SHADOW_AMBIENT_SIDE_MULTIPLIER = 0.35;

  // CANVAS BROAD WALL SHADOW
  // This is the large soft falloff on the wall.
  var CANVAS_FRAMED_BROAD_SHADOW_OPACITY = 0.34;
  var CANVAS_FRAMED_BROAD_SHADOW_BLUR_MULTIPLIER = 1.75;
  var CANVAS_FRAMED_BROAD_SHADOW_BLUR_MIN_PX = 12;
  var CANVAS_FRAMED_BROAD_SHADOW_BLUR_MAX_PX = 48;

  var CANVAS_UNFRAMED_BROAD_SHADOW_OPACITY = 0.4;
  var CANVAS_UNFRAMED_BROAD_SHADOW_BLUR_MULTIPLIER = 4.15;
  var CANVAS_UNFRAMED_BROAD_SHADOW_BLUR_MIN_PX = 10;
  var CANVAS_UNFRAMED_BROAD_SHADOW_BLUR_MAX_PX = 34;

  // CANVAS TIGHT CONTACT SHADOW
  // This is the darker close-edge shadow. For framed canvas this is the
  // main depth control.
  var CANVAS_FRAMED_CONTACT_SHADOW_OPACITY = 0.64;
  var CANVAS_FRAMED_CONTACT_SHADOW_BLUR_MULTIPLIER = 0.85;
  var CANVAS_FRAMED_CONTACT_SHADOW_BLUR_MIN_PX = 5;
  var CANVAS_FRAMED_CONTACT_SHADOW_BLUR_MAX_PX = 20;
  var CANVAS_FRAMED_CONTACT_SHADOW_OFFSET_MULTIPLIER = 1.70;

  var CANVAS_UNFRAMED_CONTACT_SHADOW_OPACITY = 0.11;
  var CANVAS_UNFRAMED_CONTACT_SHADOW_BLUR_MULTIPLIER = 0.55;
  var CANVAS_UNFRAMED_CONTACT_SHADOW_BLUR_MIN_PX = 4;
  var CANVAS_UNFRAMED_CONTACT_SHADOW_BLUR_MAX_PX = 14;
  var CANVAS_UNFRAMED_CONTACT_SHADOW_OFFSET_MULTIPLIER = 1.55;

  var CANVAS_FRAMED_SHADOW_INSET_PX = 0;
  var CANVAS_UNFRAMED_SHADOW_INSET_PX = 0.75;

  // ====================================================================
  // Utilities
  // ====================================================================

  function realPrintCm(sizeKey) {
    if (!sizeKey) return null;
    var s = String(sizeKey).toLowerCase();
    var m = s.match(/(\d+)\s*x\s*(\d+)/);
    if (!m) return null;

    var pw = parseInt(m[1], 10);
    var ph = parseInt(m[2], 10);
    if (s.indexOf('landscape') !== -1) return { w: ph, h: pw };
    return { w: pw, h: ph };
  }

  var imageCache = {};

  function normaliseUrl(raw) {
    if (!raw || typeof raw !== 'string') return '';
    var s = raw.trim();
    if (s.indexOf('//') === 0) return 'https:' + s;
    return s;
  }

  function loadImage(url) {
    var u = normaliseUrl(url);
    if (!u) return Promise.reject(new Error('empty url'));
    if (imageCache[u]) return imageCache[u];

    var p = new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () {
        if (img.naturalWidth === 0) {
          reject(new Error('zero-size image: ' + u));
        } else {
          resolve(img);
        }
      };
      img.onerror = function () { reject(new Error('image load failed: ' + u)); };
      img.src = u;
    });

    imageCache[u] = p;
    return p;
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function rgbaBlack(alpha) {
    return 'rgba(0,0,0,' + clamp(alpha, 0, 1) + ')';
  }

  function canvasCmPx(pxPerCm, cm, fallbackPx, minPx) {
    var px = pxPerCm > 0 ? cm * pxPerCm : fallbackPx;
    return Math.max(minPx || 0, px);
  }

  // ====================================================================
  // Drawing helpers
  // ====================================================================

  function drawCover(ctx, img, dx, dy, dw, dh) {
    var ir = img.naturalWidth / img.naturalHeight;
    var dr = dw / dh;
    var sw;
    var sh;
    var sx;
    var sy;

    if (ir > dr) {
      sh = img.naturalHeight;
      sw = sh * dr;
      sx = (img.naturalWidth - sw) / 2;
      sy = 0;
    } else {
      sw = img.naturalWidth;
      sh = sw / dr;
      sx = 0;
      sy = (img.naturalHeight - sh) / 2;
    }

    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  function coverRect(img, dw, dh) {
    var ir = img.naturalWidth / img.naturalHeight;
    var dr = dw / dh;
    var w;
    var h;

    if (ir > dr) {
      h = dh;
      w = dh * ir;
    } else {
      w = dw;
      h = dw / ir;
    }

    return { x: (dw - w) / 2, y: (dh - h) / 2, w: w, h: h };
  }

  function draw9Slice(ctx, img, dx, dy, dw, dh, srcInsetX, srcInsetY, dstInsetX, dstInsetY) {
    var iw = img.naturalWidth;
    var ih = img.naturalHeight;

    var sL = srcInsetX;
    var sR = iw - srcInsetX;
    var sT = srcInsetY;
    var sB = ih - srcInsetY;
    var sMidW = sR - sL;
    var sMidH = sB - sT;

    var dL = dx + dstInsetX;
    var dR = dx + dw - dstInsetX;
    var dT = dy + dstInsetY;
    var dB = dy + dh - dstInsetY;
    var dMidW = dR - dL;
    var dMidH = dB - dT;

    if (sMidW <= 0 || sMidH <= 0 || dMidW <= 0 || dMidH <= 0) {
      ctx.drawImage(img, 0, 0, iw, ih, dx, dy, dw, dh);
      return;
    }

    ctx.drawImage(img, 0, 0, srcInsetX, srcInsetY, dx, dy, dstInsetX, dstInsetY);
    ctx.drawImage(img, sR, 0, srcInsetX, srcInsetY, dR, dy, dstInsetX, dstInsetY);
    ctx.drawImage(img, 0, sB, srcInsetX, srcInsetY, dx, dB, dstInsetX, dstInsetY);
    ctx.drawImage(img, sR, sB, srcInsetX, srcInsetY, dR, dB, dstInsetX, dstInsetY);

    ctx.drawImage(img, sL, 0, sMidW, srcInsetY, dL, dy, dMidW, dstInsetY);
    ctx.drawImage(img, sL, sB, sMidW, srcInsetY, dL, dB, dMidW, dstInsetY);
    ctx.drawImage(img, 0, sT, srcInsetX, sMidH, dx, dT, dstInsetX, dMidH);
    ctx.drawImage(img, sR, sT, srcInsetX, sMidH, dR, dT, dstInsetX, dMidH);
  }

  function drawFlatMoulding(ctx, dx, dy, dw, dh, t, colors) {
    ctx.save();

    var ix = dx + t;
    var iy = dy + t;
    var iw = dw - 2 * t;
    var ih = dh - 2 * t;
    var step = Math.max(
      ART_PRINT_INNER_FRAME_SHADOW_MIN_PX,
      t * ART_PRINT_INNER_FRAME_SHADOW_WIDTH_FACTOR
    );

    var oL = dx;
    var oT = dy;
    var oR = dx + dw;
    var oB = dy + dh;
    var wL = ix;
    var wT = iy;
    var wR = ix + iw;
    var wB = iy + ih;

    function fillSide(pts, grad) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(pts[0], pts[1]);
      for (var i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
      ctx.closePath();
      ctx.clip();
      ctx.fillStyle = grad;
      ctx.fillRect(dx, dy, dw, dh);
      ctx.restore();
    }

    var gTop = ctx.createLinearGradient(0, oT, 0, wT);
    gTop.addColorStop(0, colors.light);
    gTop.addColorStop(0.5, colors.face);
    gTop.addColorStop(1, colors.dark);
    fillSide([oL, oT, oR, oT, wR, wT, wL, wT], gTop);

    var gBot = ctx.createLinearGradient(0, oB, 0, wB);
    gBot.addColorStop(0, colors.light);
    gBot.addColorStop(0.5, colors.face);
    gBot.addColorStop(1, colors.dark);
    fillSide([oL, oB, oR, oB, wR, wB, wL, wB], gBot);

    var gLeft = ctx.createLinearGradient(oL, 0, wL, 0);
    gLeft.addColorStop(0, colors.light);
    gLeft.addColorStop(0.5, colors.face);
    gLeft.addColorStop(1, colors.dark);
    fillSide([oL, oT, wL, wT, wL, wB, oL, oB], gLeft);

    var gRight = ctx.createLinearGradient(oR, 0, wR, 0);
    gRight.addColorStop(0, colors.light);
    gRight.addColorStop(0.5, colors.face);
    gRight.addColorStop(1, colors.dark);
    fillSide([oR, oT, wR, wT, wR, wB, oR, oB], gRight);

    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = colors.dark;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(oL, oT); ctx.lineTo(wL, wT);
    ctx.moveTo(oR, oT); ctx.lineTo(wR, wT);
    ctx.moveTo(oL, oB); ctx.lineTo(wL, wB);
    ctx.moveTo(oR, oB); ctx.lineTo(wR, wB);
    ctx.stroke();
    ctx.restore();

    if (iw > 0 && ih > 0) {
      var gStepTop = ctx.createLinearGradient(0, wT, 0, wT + step);
      gStepTop.addColorStop(0, rgbaBlack(ART_PRINT_INNER_FRAME_SHADOW_OPACITY));
      gStepTop.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gStepTop;
      ctx.fillRect(wL, wT, iw, step);

      var gStepBot = ctx.createLinearGradient(0, wB, 0, wB - step);
      gStepBot.addColorStop(0, rgbaBlack(ART_PRINT_INNER_FRAME_SHADOW_OPACITY));
      gStepBot.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gStepBot;
      ctx.fillRect(wL, wB - step, iw, step);

      var midH = Math.max(0, ih - 2 * step);
      if (midH > 0) {
        var gStepLeft = ctx.createLinearGradient(wL, 0, wL + step, 0);
        gStepLeft.addColorStop(0, rgbaBlack(ART_PRINT_INNER_FRAME_SHADOW_OPACITY));
        gStepLeft.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gStepLeft;
        ctx.fillRect(wL, wT + step, step, midH);

        var gStepRight = ctx.createLinearGradient(wR, 0, wR - step, 0);
        gStepRight.addColorStop(0, rgbaBlack(ART_PRINT_INNER_FRAME_SHADOW_OPACITY));
        gStepRight.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gStepRight;
        ctx.fillRect(wR - step, wT + step, step, midH);
      }
    }

    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.strokeStyle = colors.dark;
    ctx.lineWidth = 1;
    ctx.strokeRect(dx + 0.5, dy + 0.5, dw - 1, dh - 1);
    ctx.restore();

    ctx.restore();
  }

  function drawCanvasFace(ctx, img, x, y, w, h, wallRgb) {
    if (img && w > 0 && h > 0) {
      ctx.save();
      ctx.filter = 'blur(' + ART_BLUR_PX + 'px)';
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, x, y, w, h);
      ctx.filter = 'none';
      ctx.restore();
    } else {
      ctx.save();
      ctx.fillStyle = '#e9e4d8';
      ctx.fillRect(x, y, Math.max(0, w), Math.max(0, h));
      ctx.restore();
    }

    if (wallRgb && wallRgb.length === 3 && w > 0 && h > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = WALL_MATCH_ALPHA;
      ctx.fillStyle = 'rgb(' + wallRgb[0] + ',' + wallRgb[1] + ',' + wallRgb[2] + ')';
      ctx.fillRect(x, y, w, h);
      ctx.restore();
    }
  }

  function drawCanvasFloatGap(ctx, cX, cY, cW, cH, gapPx) {
    if (gapPx <= 0) return;

    var x = cX - gapPx;
    var y = cY - gapPx;
    var w = cW + 2 * gapPx;
    var h = cH + 2 * gapPx;

    ctx.save();

    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.fillRect(x, y, w, h);

    var gt = ctx.createLinearGradient(0, y, 0, cY + Math.max(1, gapPx));
    gt.addColorStop(0, 'rgba(0,0,0,0.46)');
    gt.addColorStop(1, 'rgba(0,0,0,0.10)');
    ctx.fillStyle = gt;
    ctx.fillRect(x, y, w, gapPx + 1);

    var gl = ctx.createLinearGradient(x, 0, cX + Math.max(1, gapPx), 0);
    gl.addColorStop(0, 'rgba(0,0,0,0.36)');
    gl.addColorStop(1, 'rgba(0,0,0,0.08)');
    ctx.fillStyle = gl;
    ctx.fillRect(x, y, gapPx + 1, h);

    var gb = ctx.createLinearGradient(0, cY + cH - Math.max(1, gapPx), 0, y + h);
    gb.addColorStop(0, 'rgba(0,0,0,0.05)');
    gb.addColorStop(1, 'rgba(255,255,255,0.08)');
    ctx.fillStyle = gb;
    ctx.fillRect(x, cY + cH, w, gapPx);

    ctx.globalAlpha = 0.32;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.strokeRect(cX + 0.5, cY + 0.5, cW - 1, cH - 1);

    ctx.restore();
  }

  function drawCanvasFloatFrame(ctx, cX, cY, cW, cH, framePx, gapPx, colors) {
    var grow = framePx + gapPx;
    var oX = cX - grow;
    var oY = cY - grow;
    var oW = cW + 2 * grow;
    var oH = cH + 2 * grow;

    drawFlatMoulding(ctx, oX, oY, oW, oH, framePx, colors);

    ctx.save();
    ctx.globalAlpha = 0.38;
    ctx.strokeStyle = colors.dark;
    ctx.lineWidth = 1;
    ctx.strokeRect(cX - gapPx + 0.5, cY - gapPx + 0.5, cW + 2 * gapPx - 1, cH + 2 * gapPx - 1);
    ctx.restore();
  }

  function drawCanvasFaceRecessShadow(ctx, cX, cY, cW, cH, gapPx) {
    var d = Math.max(2, gapPx * 1.4);

    ctx.save();

    var gt = ctx.createLinearGradient(0, cY, 0, cY + d);
    gt.addColorStop(0, 'rgba(0,0,0,0.26)');
    gt.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gt;
    ctx.fillRect(cX, cY, cW, d);

    var gl = ctx.createLinearGradient(cX, 0, cX + d, 0);
    gl.addColorStop(0, 'rgba(0,0,0,0.18)');
    gl.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gl;
    ctx.fillRect(cX, cY, d, cH);

    ctx.globalAlpha = 0.14;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cX, cY + cH - 0.5);
    ctx.lineTo(cX + cW, cY + cH - 0.5);
    ctx.moveTo(cX + cW - 0.5, cY);
    ctx.lineTo(cX + cW - 0.5, cY + cH);
    ctx.stroke();

    ctx.restore();
  }

  function drawCanvasObjectDropShadow(ctx, dx, dy, dw, dh, depthPx, lightDir, isFramed) {
    var d = Math.max(2, depthPx || 0);

    var sideFactor = isFramed ? CANVAS_FRAMED_SHADOW_SIDE_FACTOR : CANVAS_UNFRAMED_SHADOW_SIDE_FACTOR;
    var sideMin = isFramed ? CANVAS_FRAMED_SHADOW_SIDE_MIN_PX : CANVAS_UNFRAMED_SHADOW_SIDE_MIN_PX;
    var sideMax = isFramed ? CANVAS_FRAMED_SHADOW_SIDE_MAX_PX : CANVAS_UNFRAMED_SHADOW_SIDE_MAX_PX;

    var downFactor = isFramed ? CANVAS_FRAMED_SHADOW_DOWN_FACTOR : CANVAS_UNFRAMED_SHADOW_DOWN_FACTOR;
    var downMin = isFramed ? CANVAS_FRAMED_SHADOW_DOWN_MIN_PX : CANVAS_UNFRAMED_SHADOW_DOWN_MIN_PX;
    var downMax = isFramed ? CANVAS_FRAMED_SHADOW_DOWN_MAX_PX : CANVAS_UNFRAMED_SHADOW_DOWN_MAX_PX;

    var side = clamp(d * sideFactor, sideMin, sideMax);
    var down = clamp(d * downFactor, downMin, downMax);

    var ox;
    var oy;

    if (lightDir === 'left') {
      ox = side;
      oy = down;
    } else if (lightDir === 'right') {
      ox = -side;
      oy = down;
    } else if (lightDir === 'top') {
      ox = 0;
      oy = down * CANVAS_SHADOW_TOP_DOWN_MULTIPLIER;
    } else {
      ox = side * CANVAS_SHADOW_AMBIENT_SIDE_MULTIPLIER;
      oy = down;
    }

    var inset = isFramed ? CANVAS_FRAMED_SHADOW_INSET_PX : CANVAS_UNFRAMED_SHADOW_INSET_PX;

    var broadOpacity = isFramed ? CANVAS_FRAMED_BROAD_SHADOW_OPACITY : CANVAS_UNFRAMED_BROAD_SHADOW_OPACITY;
    var broadBlurMultiplier = isFramed ? CANVAS_FRAMED_BROAD_SHADOW_BLUR_MULTIPLIER : CANVAS_UNFRAMED_BROAD_SHADOW_BLUR_MULTIPLIER;
    var broadBlurMin = isFramed ? CANVAS_FRAMED_BROAD_SHADOW_BLUR_MIN_PX : CANVAS_UNFRAMED_BROAD_SHADOW_BLUR_MIN_PX;
    var broadBlurMax = isFramed ? CANVAS_FRAMED_BROAD_SHADOW_BLUR_MAX_PX : CANVAS_UNFRAMED_BROAD_SHADOW_BLUR_MAX_PX;

    var contactOpacity = isFramed ? CANVAS_FRAMED_CONTACT_SHADOW_OPACITY : CANVAS_UNFRAMED_CONTACT_SHADOW_OPACITY;
    var contactBlurMultiplier = isFramed ? CANVAS_FRAMED_CONTACT_SHADOW_BLUR_MULTIPLIER : CANVAS_UNFRAMED_CONTACT_SHADOW_BLUR_MULTIPLIER;
    var contactBlurMin = isFramed ? CANVAS_FRAMED_CONTACT_SHADOW_BLUR_MIN_PX : CANVAS_UNFRAMED_CONTACT_SHADOW_BLUR_MIN_PX;
    var contactBlurMax = isFramed ? CANVAS_FRAMED_CONTACT_SHADOW_BLUR_MAX_PX : CANVAS_UNFRAMED_CONTACT_SHADOW_BLUR_MAX_PX;
    var contactOffsetMultiplier = isFramed ? CANVAS_FRAMED_CONTACT_SHADOW_OFFSET_MULTIPLIER : CANVAS_UNFRAMED_CONTACT_SHADOW_OFFSET_MULTIPLIER;

    ctx.save();
    ctx.shadowColor = rgbaBlack(broadOpacity);
    ctx.shadowBlur = clamp(d * broadBlurMultiplier, broadBlurMin, broadBlurMax);
    ctx.shadowOffsetX = ox;
    ctx.shadowOffsetY = oy;
    ctx.fillStyle = '#000000';
    ctx.fillRect(dx + inset, dy + inset, Math.max(0, dw - 2 * inset), Math.max(0, dh - 2 * inset));
    ctx.restore();

    ctx.save();
    ctx.shadowColor = rgbaBlack(contactOpacity);
    ctx.shadowBlur = clamp(d * contactBlurMultiplier, contactBlurMin, contactBlurMax);
    ctx.shadowOffsetX = ox * contactOffsetMultiplier;
    ctx.shadowOffsetY = Math.max(1, oy * contactOffsetMultiplier);
    ctx.fillStyle = '#000000';
    ctx.fillRect(dx + inset, dy + inset, Math.max(0, dw - 2 * inset), Math.max(0, dh - 2 * inset));
    ctx.restore();
  }

  function drawUnframedCanvasSubtleDepth(ctx, cX, cY, cW, cH, depthPx) {
    var e = clamp(depthPx * 0.22, 2, 6);

    ctx.save();

    var gr = ctx.createLinearGradient(cX + cW - 0.5, 0, cX + cW + e, 0);
    gr.addColorStop(0, 'rgba(0,0,0,0.16)');
    gr.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gr;
    ctx.fillRect(cX + cW - 0.5, cY + 1, e, cH + Math.min(2, e));

    var gb = ctx.createLinearGradient(0, cY + cH - 0.5, 0, cY + cH + e);
    gb.addColorStop(0, 'rgba(0,0,0,0.15)');
    gb.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gb;
    ctx.fillRect(cX + 1, cY + cH - 0.5, cW + Math.min(2, e), e);

    var gt = ctx.createLinearGradient(0, cY, 0, cY + e);
    gt.addColorStop(0, 'rgba(0,0,0,0.12)');
    gt.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gt;
    ctx.fillRect(cX, cY, cW, e);

    var gl = ctx.createLinearGradient(cX, 0, cX + e, 0);
    gl.addColorStop(0, 'rgba(0,0,0,0.08)');
    gl.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gl;
    ctx.fillRect(cX, cY, e, cH);

    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cX + 1, cY + cH - 0.5);
    ctx.lineTo(cX + cW - 1, cY + cH - 0.5);
    ctx.moveTo(cX + cW - 0.5, cY + 1);
    ctx.lineTo(cX + cW - 0.5, cY + cH - 1);
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 1;
    ctx.strokeRect(cX + 0.5, cY + 0.5, cW - 1, cH - 1);

    ctx.restore();
  }

  function drawArtPrintWallIntegration(ctx, dx, dy, dw, dh, wallRgb, wallLum, isFramed) {
    if (!wallRgb || wallRgb.length !== 3 || dw <= 0 || dh <= 0) return;

    var lum = typeof wallLum === 'number' ? clamp(wallLum, 0, 1) : 0.5;
    var mult = isFramed
      ? ART_PRINT_WALL_INTEGRATION_FRAMED_MULT
      : ART_PRINT_WALL_INTEGRATION_UNFRAMED_MULT;

    var rgb = 'rgb(' + wallRgb[0] + ',' + wallRgb[1] + ',' + wallRgb[2] + ')';

    // Main ambient colour cast from the wall.
    ctx.save();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = ART_PRINT_WALL_SOFTLIGHT_ALPHA * mult;
    ctx.fillStyle = rgb;
    ctx.fillRect(dx, dy, dw, dh);
    ctx.restore();

    // Tiny absorption layer. Helps reduce the clean digital cutout look.
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = (ART_PRINT_WALL_MULTIPLY_ALPHA + (1 - lum) * 0.018) * mult;
    ctx.fillStyle = rgb;
    ctx.fillRect(dx, dy, dw, dh);
    ctx.restore();

    // On lighter walls, add a tiny room-light lift so whites don't look pasted.
    if (lum > 0.5) {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = ((lum - 0.5) * 2) * ART_PRINT_WALL_SCREEN_ALPHA * mult;
      ctx.fillStyle = rgb;
      ctx.fillRect(dx, dy, dw, dh);
      ctx.restore();
    }
  }

  function drawContactShadow(ctx, dx, dy, dw, dh, mouldInset, lightDir, wallLum) {
    ctx.save();

    var isFramedPrint = (mouldInset || 0) > 0;
    var offsetMult = isFramedPrint ? ART_PRINT_FRAMED_SHADOW_OFFSET_MULT : 1;
    var down = dh * ART_PRINT_SHADOW_DOWN_FACTOR * offsetMult;
    var side = dw * ART_PRINT_SHADOW_SIDE_FACTOR * offsetMult;
    var ox;
    var oy;

    if (lightDir === 'left') {
      ox = side;
      oy = down;
    } else if (lightDir === 'right') {
      ox = -side;
      oy = down;
    } else if (lightDir === 'top') {
      ox = 0;
      oy = down * ART_PRINT_SHADOW_TOP_DOWN_MULTIPLIER;
    } else {
      ox = dw * ART_PRINT_SHADOW_AMBIENT_SIDE_FACTOR * offsetMult;
      oy = down;
    }

    var pad = Math.max(
      ART_PRINT_SHADOW_PAD_MIN_PX,
      (mouldInset || 0) * ART_PRINT_SHADOW_PAD_MOULDING_FACTOR
    );

    var shadowOpacity = ART_PRINT_SHADOW_OPACITY;
    if (isFramedPrint) shadowOpacity *= ART_PRINT_FRAMED_SHADOW_OPACITY_MULT;

    var lum = typeof wallLum === 'number' ? clamp(wallLum, 0, 1) : 0.5;
    shadowOpacity *= 1 + ((lum - 0.5) * ART_PRINT_SHADOW_WALL_LUM_RESPONSE);

    var blurMult = isFramedPrint ? ART_PRINT_FRAMED_SHADOW_BLUR_MULT : 1;
    ctx.shadowColor = rgbaBlack(shadowOpacity);
    ctx.shadowBlur = Math.max(ART_PRINT_SHADOW_BLUR_MIN_PX, dw * ART_PRINT_SHADOW_BLUR_WIDTH_FACTOR) * blurMult;
    ctx.shadowOffsetX = ox;
    ctx.shadowOffsetY = oy;
    ctx.fillStyle = '#000000';
    ctx.fillRect(dx + pad, dy + pad, Math.max(0, dw - 2 * pad), Math.max(0, dh - 2 * pad));

    ctx.restore();
  }

  function drawLightingOverlay(ctx, dx, dy, dw, dh, lightDir, opacity) {
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = typeof opacity === 'number' ? opacity : LIGHT_OPACITY;

    var grad;

    if (lightDir === 'left') {
      grad = ctx.createLinearGradient(dx, dy, dx + dw, dy);
      grad.addColorStop(0, 'rgba(255,255,255,0)');
      grad.addColorStop(1, 'rgba(40,30,15,1)');
    } else if (lightDir === 'right') {
      grad = ctx.createLinearGradient(dx, dy, dx + dw, dy);
      grad.addColorStop(0, 'rgba(40,30,15,1)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
    } else if (lightDir === 'top') {
      grad = ctx.createLinearGradient(dx, dy, dx, dy + dh);
      grad.addColorStop(0, 'rgba(255,255,255,0)');
      grad.addColorStop(1, 'rgba(40,30,15,1)');
    } else {
      grad = ctx.createLinearGradient(dx, dy, dx, dy + dh);
      grad.addColorStop(0, 'rgba(255,255,255,0)');
      grad.addColorStop(1, 'rgba(40,30,15,0.6)');
    }

    ctx.fillStyle = grad;
    ctx.fillRect(dx, dy, dw, dh);
    ctx.restore();
  }

  // ====================================================================
  // Compositor
  // ====================================================================

  function FabRoomCompositor(stageEl) {
    this.stage = stageEl;
    this.canvas = null;
    this._renderToken = 0;
    this._lastGeometry = null;
  }

  FabRoomCompositor.prototype._ensureCanvas = function () {
    if (!this.canvas) {
      var c = document.createElement('canvas');
      c.className = 'fab-size__compositor-canvas';
      c.setAttribute('data-fab-compositor-canvas', '');
      c.setAttribute('aria-hidden', 'true');
      c.style.position = 'absolute';
      c.style.inset = '0';
      c.style.width = '100%';
      c.style.height = '100%';
      c.style.zIndex = '1';
      c.style.pointerEvents = 'none';
      c.style.opacity = '0';
      this.stage.appendChild(c);
      this.canvas = c;
    }

    var rect = this.stage.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.round(rect.width);
    var h = Math.round(rect.height);

    if (w === 0 || h === 0) return null;

    if (this.canvas.width !== w * dpr || this.canvas.height !== h * dpr) {
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
    }

    var ctx = this.canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._cssW = w;
    this._cssH = h;
    return ctx;
  };

  FabRoomCompositor.prototype.show = function () {
    if (this.canvas) this.canvas.style.opacity = '1';
  };

  FabRoomCompositor.prototype.hide = function () {
    if (this.canvas) this.canvas.style.opacity = '0';
  };

  FabRoomCompositor.prototype.getPieceRect = function () {
    return this._lastGeometry;
  };

  FabRoomCompositor.prototype.render = function (opts) {
    var self = this;
    var token = ++this._renderToken;
    opts = opts || {};

    if (!opts || !opts.scene || !opts.scene.image) {
      return Promise.resolve(false);
    }

    var scene = opts.scene;
    var frameColor = (opts.frameColor || '').toLowerCase().trim();
    var isFramed = frameColor && frameColor !== 'unframed';

    var waShape = scene.wallArea || { width: 60, height: 80 };
    var orientation = (Number(waShape.width) || 1) >= (Number(waShape.height) || 1)
      ? 'landscape'
      : 'portrait';

    var framePngUrl = '';
    if (isFramed && !opts.isCanvas && FRAME_PNGS[frameColor]) {
      framePngUrl = FRAME_PNGS[frameColor][orientation] || '';
    }

    var jobs = [
      loadImage(scene.image).then(
        function (i) { return { room: i }; },
        function () { return { room: null }; }
      ),
      loadImage(opts.artUrl).then(
        function (i) { return { art: i }; },
        function () { return { art: null }; }
      )
    ];

    if (framePngUrl) {
      jobs.push(loadImage(framePngUrl).then(
        function (i) { return { frame: i }; },
        function () { return { frame: null }; }
      ));
    }

    if (scene.lightingImage) {
      jobs.push(loadImage(scene.lightingImage).then(
        function (i) { return { light: i }; },
        function () { return { light: null }; }
      ));
    }

    return Promise.all(jobs).then(function (results) {
      if (token !== self._renderToken) return 'stale';

      var bag = {};
      results.forEach(function (r) { Object.assign(bag, r); });
      if (!bag.room) return false;

      var ctx = self._ensureCanvas();
      if (!ctx) return false;

      var W = self._cssW;
      var H = self._cssH;
      ctx.clearRect(0, 0, W, H);

      drawCover(ctx, bag.room, 0, 0, W, H);

      var photoRect = coverRect(bag.room, W, H);
      var wa = scene.wallArea || { x: 0, y: 0, width: 60, height: 60 };

      var artX = photoRect.x + (wa.x / 100) * photoRect.w;
      var artY = photoRect.y + (wa.y / 100) * photoRect.h;
      var artW = (wa.width / 100) * photoRect.w;
      var artH = (wa.height / 100) * photoRect.h;

      var pxPerCm = 0;
      var photoCm = Number(wa.photoCm) || Number(scene.photoCm) || 0;

      if (photoCm > 0) {
        pxPerCm = photoRect.w / photoCm;
      } else {
        var cmFallback = realPrintCm(scene.size);
        if (cmFallback && cmFallback.w > 0) pxPerCm = artW / cmFallback.w;
      }

      if (opts.isCanvas) {
        var wallRgbForCanvas = (scene.wallArea && scene.wallArea.wallRgb) || null;
        var canvasWallLum = (scene.wallArea && typeof scene.wallArea.wallLum === 'number')
          ? scene.wallArea.wallLum
          : null;
        var minCanvasEdge = Math.min(artW, artH);

        var canvasFramePx = canvasCmPx(pxPerCm, CANVAS_FLOAT_FRAME_CM, minCanvasEdge * 0.018, 1.25);
        var canvasGapPx = canvasCmPx(pxPerCm, CANVAS_FLOAT_GAP_CM, minCanvasEdge * 0.014, 1.0);

        var canvasShadowDepthCm = isFramed
          ? CANVAS_FRAMED_SHADOW_DEPTH_CM
          : CANVAS_UNFRAMED_SHADOW_DEPTH_CM;

        var canvasShadowDepthFallbackFactor = isFramed
          ? CANVAS_FRAMED_SHADOW_DEPTH_FALLBACK_FACTOR
          : CANVAS_UNFRAMED_SHADOW_DEPTH_FALLBACK_FACTOR;

        var canvasDepthPx = canvasCmPx(
          pxPerCm,
          canvasShadowDepthCm,
          minCanvasEdge * canvasShadowDepthFallbackFactor,
          2.0
        );

        var cOutX;
        var cOutY;
        var cOutW;
        var cOutH;
        var lightDirCanvas = scene.lightDirection;

        if (isFramed) {
          var canvasGrow = canvasFramePx + canvasGapPx;
          cOutX = artX - canvasGrow;
          cOutY = artY - canvasGrow;
          cOutW = artW + 2 * canvasGrow;
          cOutH = artH + 2 * canvasGrow;

          self._lastGeometry = { x: cOutX, y: cOutY, w: cOutW, h: cOutH };

          drawCanvasObjectDropShadow(ctx, cOutX, cOutY, cOutW, cOutH, canvasDepthPx, lightDirCanvas, true);
          drawCanvasFloatGap(ctx, artX, artY, artW, artH, canvasGapPx);
          drawCanvasFace(ctx, bag.art, artX, artY, artW, artH, wallRgbForCanvas);

          var canvasColors = FLAT_MOULDING[frameColor] || FLAT_MOULDING.black;
          drawCanvasFloatFrame(ctx, artX, artY, artW, artH, canvasFramePx, canvasGapPx, canvasColors);
          drawCanvasFaceRecessShadow(ctx, artX, artY, artW, artH, canvasGapPx);
        } else {
          cOutX = artX;
          cOutY = artY;
          cOutW = artW;
          cOutH = artH;

          self._lastGeometry = { x: artX, y: artY, w: artW, h: artH };

          drawCanvasObjectDropShadow(ctx, artX, artY, artW, artH, canvasDepthPx, lightDirCanvas, false);
          drawCanvasFace(ctx, bag.art, artX, artY, artW, artH, wallRgbForCanvas);
          drawUnframedCanvasSubtleDepth(ctx, artX, artY, artW, artH, canvasDepthPx);
        }

        drawArtPrintWallIntegration(
          ctx,
          cOutX,
          cOutY,
          cOutW,
          cOutH,
          wallRgbForCanvas,
          canvasWallLum,
          isFramed
        );

        if (bag.light) {
          ctx.save();
          ctx.globalCompositeOperation = 'multiply';
          ctx.globalAlpha = LIGHT_OPACITY * 1.25;
          drawCover(ctx, bag.light, cOutX, cOutY, cOutW, cOutH);
          ctx.restore();
        } else {
          var canvasLum = LIGHT_OPACITY;
          if (canvasWallLum !== null) canvasLum = LIGHT_OPACITY + LIGHT_LUM_BOOST * canvasWallLum;
          drawLightingOverlay(ctx, cOutX, cOutY, cOutW, cOutH, lightDirCanvas, canvasLum);
        }
      } else {
      var mouldPx = 0;
      if (isFramed) {
        if (pxPerCm > 0) {
          mouldPx = MOULDING_CM * pxPerCm;
        } else {
          mouldPx = Math.min(artW, artH) * FRAME_PNG_MOULDING_FRACTION;
        }
      }

      var pieceX = artX - mouldPx;
      var pieceY = artY - mouldPx;
      var pieceW = artW + 2 * mouldPx;
      var pieceH = artH + 2 * mouldPx;

      self._lastGeometry = { x: pieceX, y: pieceY, w: pieceW, h: pieceH };

      var lightDir = scene.lightDirection;

      var artPrintWallRgb = (scene.wallArea && scene.wallArea.wallRgb) || null;
      var artPrintWallLum = (scene.wallArea && typeof scene.wallArea.wallLum === 'number')
        ? scene.wallArea.wallLum
        : null;

      drawContactShadow(
        ctx,
        pieceX,
        pieceY,
        pieceW,
        pieceH,
        mouldPx,
        lightDir,
        artPrintWallLum
      );

      var fullBleed = opts.fullBleed !== false;

      if (!fullBleed) {
        ctx.save();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(artX, artY, Math.max(0, artW), Math.max(0, artH));
        ctx.restore();
      }

      var borderPx = fullBleed ? 0 : Math.min(artW, artH) * PAPER_BORDER_FRACTION;
      var imgX = artX + borderPx;
      var imgY = artY + borderPx;
      var imgW = artW - 2 * borderPx;
      var imgH = artH - 2 * borderPx;

      if (bag.art && imgW > 0 && imgH > 0) {
        ctx.save();
        ctx.filter = 'blur(' + ART_BLUR_PX + 'px)';
        ctx.drawImage(bag.art, 0, 0, bag.art.naturalWidth, bag.art.naturalHeight, imgX, imgY, imgW, imgH);
        ctx.filter = 'none';
        ctx.restore();
      } else {
        ctx.save();
        ctx.fillStyle = '#e9e4d8';
        ctx.fillRect(imgX, imgY, Math.max(0, imgW), Math.max(0, imgH));
        ctx.restore();
      }

      var wallRgb = artPrintWallRgb;
      if (wallRgb && wallRgb.length === 3 && imgW > 0 && imgH > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = WALL_MATCH_ALPHA;
        ctx.fillStyle = 'rgb(' + wallRgb[0] + ',' + wallRgb[1] + ',' + wallRgb[2] + ')';
        ctx.fillRect(imgX, imgY, imgW, imgH);
        ctx.restore();
      }

      if (isFramed) {
        if (framePngUrl && bag.frame) {
          var srcInset = Math.min(bag.frame.naturalWidth, bag.frame.naturalHeight) * FRAME_PNG_MOULDING_FRACTION;
          draw9Slice(ctx, bag.frame, pieceX, pieceY, pieceW, pieceH, srcInset, srcInset, mouldPx, mouldPx);
        } else {
          var colors = FLAT_MOULDING[frameColor] || FLAT_MOULDING.black;
          drawFlatMoulding(ctx, pieceX, pieceY, pieceW, pieceH, mouldPx, colors);
        }
      } else {
        ctx.save();
        ctx.strokeStyle = 'rgba(0,0,0,' + EDGE_FEATHER_ALPHA + ')';
        ctx.lineWidth = 1;
        ctx.strokeRect(artX + 0.5, artY + 0.5, artW - 1, artH - 1);
        ctx.restore();
      }

      drawArtPrintWallIntegration(
        ctx,
        pieceX,
        pieceY,
        pieceW,
        pieceH,
        artPrintWallRgb,
        artPrintWallLum,
        isFramed
      );

      if (bag.light) {
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = LIGHT_OPACITY * 1.4;
        drawCover(ctx, bag.light, pieceX, pieceY, pieceW, pieceH);
        ctx.restore();
      } else {
        var wallLum = artPrintWallLum;
        var lumScale = LIGHT_OPACITY;
        if (wallLum !== null) lumScale = LIGHT_OPACITY + LIGHT_LUM_BOOST * wallLum;
        drawLightingOverlay(ctx, pieceX, pieceY, pieceW, pieceH, lightDir, lumScale);
      }
      }

      if (token !== self._renderToken) return 'stale';
      return true;
    }).catch(function () {
      return false;
    });
  };

  FabRoomCompositor.prototype.clear = function () {
    this._renderToken += 1;
    if (this.canvas) {
      var ctx = this.canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    this._lastGeometry = null;
    this.hide();
  };

  FabRoomCompositor.setFramePngs = function (map) {
    if (!map || typeof map !== 'object') return;

    Object.keys(map).forEach(function (k) {
      var key = k.toLowerCase();
      if (FRAME_PNGS[key]) {
        if (map[k].portrait != null) FRAME_PNGS[key].portrait = map[k].portrait;
        if (map[k].landscape != null) FRAME_PNGS[key].landscape = map[k].landscape;
      }
    });
  };

  FabRoomCompositor.setFramePngMouldingFraction = function (f) {
    var n = Number(f);
    if (n > 0 && n < 0.5) FRAME_PNG_MOULDING_FRACTION = n;
  };

  window.FabRoomCompositor = FabRoomCompositor;
})();